import { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import { useClerk } from '@clerk/clerk-react';
import {
  AutocompleteInput,
  BooleanInput,
  CreateButton,
  Datagrid,
  DateInput,
  EditButton,
  FunctionField,
  List,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  ShowButton,
  TextField,
  TextInput,
  useGetOne,
  useListContext,
} from 'react-admin';

import { DataTableFilterControls } from 'src/client/components/data-table-list-actions';
import { ESTDateField } from 'src/client/components/custom-ra-fields/est-date-field';
import { NEW_YORK_TIMEZONE, includesInsensitive } from 'src/client/components/forms/form-utils';
import { formatCampaignTitle } from '../campaigns/CampaignList';
import {
  LineItemRecord,
  formatCurrency,
  formatLineItemTitle,
  invoiceStatusChoices,
  lineItemStatusChoices,
} from './LineItemCreate';
import { DuplicateLineItemButton } from './DuplicateLineItemButton';
import { LineItemArchiveButton } from './LineItemArchiveButton';

const dateOnOrAfterFilter = {
  parse: (value?: string) =>
    value
      ? {
          operator: 'greaterThanOrEqualTo',
          value,
        }
      : undefined,
  format: (value?: string | { value?: string }) => (typeof value === 'string' ? value : value?.value ?? ''),
};

// Per PIO migration spec: `reviewed_by` NULL means self-reviewed, not unreviewed.
// PV-18 frames the gap as "blank or populated", so the filter exposes those two states.
const reviewedFilter = {
  parse: (value?: string) => {
    if (value === 'populated') return { operator: 'isNull', value: false };
    if (value === 'blank') return { operator: 'isNull', value: true };

    return undefined;
  },
  format: (value?: string | { operator?: string; value?: boolean }) => {
    if (value === 'populated' || value === 'blank') return value;
    if (typeof value === 'string') return '';
    if (value?.operator === 'isNull') return value.value === false ? 'populated' : 'blank';

    return '';
  },
};

const reviewedChoices = [
  { id: 'populated', name: 'Has reviewer' },
  // Labeled "Blank reviewer" rather than "Self-reviewed" to disambiguate from the
  // view selector's "Recent · self-reviewed" — that view scopes blank-reviewer
  // rows to the 7-day window, whereas this drawer choice is the raw is-null filter.
  { id: 'blank', name: 'Blank reviewer' },
];

const RECENT_LINE_ITEM_DAYS = 7;
const RECENT_LINE_ITEM_WINDOW_MS = RECENT_LINE_ITEM_DAYS * 24 * 60 * 60 * 1000;

// Sentinel filter key that marks the recent-7-days quick chip as active. Using a
// dedicated boolean (rather than detecting the createdAt window) keeps the chip
// state distinguishable from a user-set createdAt filter that happens to land in
// the same window. The postgraphile data provider strips this key before
// delegating to GraphQL (see providers/postgraphile-data-provider.ts).
export const RECENT_LINE_ITEM_FILTER_FLAG = '_recentLineItemsChip' as const;

type LineItemFilterSpec = {
  operator: 'greaterThanOrEqualTo' | 'isNull';
  value: string | boolean;
};

type LineItemFilterValues = Record<string, unknown> & {
  createdAt?: LineItemFilterSpec;
  externalPioId?: LineItemFilterSpec;
  specialty?: string | { operator?: string; value?: string };
  status?: string;
  reviewedBy?: LineItemFilterSpec;
  [RECENT_LINE_ITEM_FILTER_FLAG]?: boolean | string;
};

export const getRecentLineItemCreatedAtFilter = (now = new Date()): LineItemFilterSpec => ({
  operator: 'greaterThanOrEqualTo',
  value: new Date(now.getTime() - RECENT_LINE_ITEM_WINDOW_MS).toISOString(),
});

export const getCsadminCreatedLineItemFilter = (): LineItemFilterSpec => ({
  operator: 'isNull',
  value: true,
});

const isNullFilter = (value: unknown): value is LineItemFilterSpec =>
  typeof value === 'object' &&
  value !== null &&
  (value as LineItemFilterSpec).operator === 'isNull' &&
  (value as LineItemFilterSpec).value === true;

const isReviewedByNullFilter = isNullFilter;
const isCsadminCreatedLineItemFilter = isNullFilter;

export const isRecentLineItemFilterActive = (filters: LineItemFilterValues) =>
  filters[RECENT_LINE_ITEM_FILTER_FLAG] === true || filters[RECENT_LINE_ITEM_FILTER_FLAG] === 'true';

export const isSelfReviewedRecentLineItemFilterActive = (filters: LineItemFilterValues) =>
  isRecentLineItemFilterActive(filters) && isReviewedByNullFilter(filters.reviewedBy);

export const addRecentLineItemFilter = (filters: LineItemFilterValues, now = new Date()): LineItemFilterValues => ({
  ...filters,
  createdAt: getRecentLineItemCreatedAtFilter(now),
  externalPioId: getCsadminCreatedLineItemFilter(),
  [RECENT_LINE_ITEM_FILTER_FLAG]: true,
});

export const removeRecentLineItemFilter = (filters: LineItemFilterValues): LineItemFilterValues => {
  if (!isRecentLineItemFilterActive(filters)) return filters;

  const { createdAt: _createdAt, [RECENT_LINE_ITEM_FILTER_FLAG]: _flag, ...restFilters } = filters;
  let remainingFilters = restFilters;

  if (isCsadminCreatedLineItemFilter(remainingFilters.externalPioId)) {
    const { externalPioId: _externalPioId, ...filtersWithoutNativeScope } = remainingFilters;
    remainingFilters = filtersWithoutNativeScope;
  }

  if (isReviewedByNullFilter(remainingFilters.reviewedBy)) {
    const { reviewedBy: _reviewedBy, ...filtersWithoutSelfReviewed } = remainingFilters;
    return filtersWithoutSelfReviewed;
  }

  return remainingFilters;
};

export const addSelfReviewedRecentLineItemFilter = (
  filters: LineItemFilterValues,
  now = new Date(),
): LineItemFilterValues => ({
  ...addRecentLineItemFilter(filters, now),
  reviewedBy: { operator: 'isNull', value: true },
});

export const removeSelfReviewedLineItemFilter = (filters: LineItemFilterValues): LineItemFilterValues => {
  // Only strip `reviewedBy` when it's the blank-reviewer null sentinel that the
  // self-reviewed view sets. A user-set reviewer filter from the More filters
  // drawer (e.g. "Has reviewer" = isNull:false) must survive view transitions.
  if (!isReviewedByNullFilter(filters.reviewedBy)) return filters;
  const { reviewedBy: _reviewedBy, ...restFilters } = filters;
  return restFilters;
};

export const LINE_ITEM_VIEW_ALL = 'all';
export const LINE_ITEM_VIEW_RECENT = 'recent';
export const LINE_ITEM_VIEW_RECENT_SELF_REVIEWED = 'recent-self-reviewed';

export type LineItemViewId =
  | typeof LINE_ITEM_VIEW_ALL
  | typeof LINE_ITEM_VIEW_RECENT
  | typeof LINE_ITEM_VIEW_RECENT_SELF_REVIEWED;

export const getActiveLineItemViewId = (filters: LineItemFilterValues): LineItemViewId => {
  if (isSelfReviewedRecentLineItemFilterActive(filters)) return LINE_ITEM_VIEW_RECENT_SELF_REVIEWED;
  if (isRecentLineItemFilterActive(filters)) return LINE_ITEM_VIEW_RECENT;
  return LINE_ITEM_VIEW_ALL;
};

export const applyLineItemViewId = (
  filters: LineItemFilterValues,
  viewId: LineItemViewId,
  now = new Date(),
): LineItemFilterValues => {
  switch (viewId) {
    case LINE_ITEM_VIEW_RECENT_SELF_REVIEWED:
      return addSelfReviewedRecentLineItemFilter(filters, now);
    case LINE_ITEM_VIEW_RECENT:
      return removeSelfReviewedLineItemFilter(addRecentLineItemFilter(filters, now));
    case LINE_ITEM_VIEW_ALL:
    default:
      return removeRecentLineItemFilter(filters);
  }
};

const RECENT_WINDOW_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  timeZone: NEW_YORK_TIMEZONE,
};

const formatRecentWindowSince = (createdAt?: LineItemFilterSpec): string | null => {
  if (!createdAt || createdAt.operator !== 'greaterThanOrEqualTo' || typeof createdAt.value !== 'string') return null;

  const parsed = new Date(createdAt.value);
  if (Number.isNaN(parsed.getTime())) return null;

  return new Intl.DateTimeFormat('en-US', RECENT_WINDOW_DATE_FORMAT).format(parsed);
};

const formatChoice = (choices: { id: string; name: string }[], value?: string | null) =>
  choices.find(choice => choice.id === value)?.name ?? value ?? '-';

type RemovableFilterChip = {
  key: string;
  label: string;
  // When set, the chip label is upgraded from the raw id to the referenced
  // record's name once it loads (see FilterChip).
  reference?: { resource: string; id: string; prefix: string };
};

const getStringFilterValue = (value: unknown) => {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'object' && value !== null && typeof (value as { value?: unknown }).value === 'string') {
    return (value as { value: string }).value.trim();
  }
  return '';
};

export const getLineItemExtraFilterChips = (filters: LineItemFilterValues): RemovableFilterChip[] => {
  const chips: RemovableFilterChip[] = [];

  if (typeof filters.status === 'string' && filters.status) {
    chips.push({ key: 'status', label: `Status: ${formatChoice(lineItemStatusChoices, filters.status)}` });
  }

  const specialtyValue = getStringFilterValue(filters.specialty);
  if (specialtyValue) {
    chips.push({ key: 'specialty', label: `Specialty: ${specialtyValue}` });
  }

  if (filters.startDate && typeof (filters.startDate as { value?: unknown }).value === 'string') {
    chips.push({ key: 'startDate', label: `Starting on / after: ${(filters.startDate as { value: string }).value}` });
  }

  if (typeof filters.campaignId === 'string' && filters.campaignId) {
    chips.push({
      key: 'campaignId',
      label: `Campaign: ${filters.campaignId}`,
      reference: { resource: 'campaigns', id: filters.campaignId, prefix: 'Campaign' },
    });
  }

  if (typeof filters.advertiserId === 'string' && filters.advertiserId) {
    chips.push({
      key: 'advertiserId',
      label: `Advertiser: ${filters.advertiserId}`,
      reference: { resource: 'advertisers', id: filters.advertiserId, prefix: 'Advertiser' },
    });
  }

  if (filters.reviewedBy && !isSelfReviewedRecentLineItemFilterActive(filters)) {
    const reviewerFilter = reviewedFilter.format(
      filters.reviewedBy as string | { operator?: string; value?: boolean },
    );
    chips.push({ key: 'reviewedBy', label: `Reviewer: ${reviewerFilter === 'populated' ? 'has reviewer' : 'blank'}` });
  }

  return chips;
};

type LineItemListRecord = LineItemRecord & {
  reviewedBy?: string | null;
  pioUserByReviewedBy?: {
    name?: string | null;
  } | null;
};

export const lineItemFilters = [
  <TextInput key="name" label="Line Item Name" source="name" {...includesInsensitive} alwaysOn resettable />,
  <NumberInput key="lineItemNumber" label="Line Item Number" source="lineItemNumber" alwaysOn />,
  <ReferenceInput
    key="campaignId"
    source="campaignId"
    reference="campaigns"
    sort={{ field: 'campaignNumber', order: 'DESC' }}
  >
    <AutocompleteInput label="Campaign" optionText={formatCampaignTitle} />
  </ReferenceInput>,
  <ReferenceInput
    key="advertiserId"
    source="advertiserId"
    reference="advertisers"
    filter={{ type: 'ADVERTISER' }}
    sort={{ field: 'name', order: 'ASC' }}
    perPage={50}
  >
    <AutocompleteInput
      label="Advertiser"
      optionText="name"
      filterToQuery={searchText => ({ name: { operator: 'includesInsensitive', value: searchText } })}
      shouldRenderSuggestions={() => true}
    />
  </ReferenceInput>,
  <TextInput key="specialty" label="Specialty" source="specialty" {...includesInsensitive} resettable />,
  <SelectInput key="status" label="Status" source="status" choices={lineItemStatusChoices} />,
  <DateInput key="startDate" label="Starting On / After" source="startDate" {...dateOnOrAfterFilter} />,
  <SelectInput key="reviewedBy" label="Reviewer" source="reviewedBy" choices={reviewedChoices} {...reviewedFilter} />,
  <BooleanInput key="includeArchived" label="Include Archived" source="includeArchived" alwaysOn />,
];

const VIEW_TOOLTIPS: Record<LineItemViewId, string> = {
  [LINE_ITEM_VIEW_ALL]: 'Show every active line item.',
  [LINE_ITEM_VIEW_RECENT]: 'CSAdmin-created line items from the last 7 days — the retrospective review queue.',
  [LINE_ITEM_VIEW_RECENT_SELF_REVIEWED]:
    'CSAdmin-created line items from the last 7 days with no reviewer assigned. Imported PIO history is excluded from this review queue.',
};

const LineItemViewSelector = () => {
  const { displayedFilters, filterValues, setFilters } = useListContext();
  const filters = (filterValues ?? {}) as LineItemFilterValues;
  const activeViewId = getActiveLineItemViewId(filters);

  const handleChange = (_event: unknown, nextViewId: LineItemViewId | null) => {
    if (!nextViewId || nextViewId === activeViewId) return;
    setFilters(applyLineItemViewId(filters, nextViewId), displayedFilters);
  };

  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={activeViewId}
      onChange={handleChange}
      aria-label="Line item review view"
    >
      <Tooltip title={VIEW_TOOLTIPS[LINE_ITEM_VIEW_ALL]} placement="bottom" arrow>
        <ToggleButton value={LINE_ITEM_VIEW_ALL} aria-label="All line items">
          All
        </ToggleButton>
      </Tooltip>
      <Tooltip title={VIEW_TOOLTIPS[LINE_ITEM_VIEW_RECENT]} placement="bottom" arrow>
        <ToggleButton value={LINE_ITEM_VIEW_RECENT} aria-label="Recent line items (last 7 days)">
          Recent (7d)
        </ToggleButton>
      </Tooltip>
      <Tooltip title={VIEW_TOOLTIPS[LINE_ITEM_VIEW_RECENT_SELF_REVIEWED]} placement="bottom" arrow>
        <ToggleButton value={LINE_ITEM_VIEW_RECENT_SELF_REVIEWED} aria-label="Recent line items with blank reviewer">
          Recent · self-reviewed
        </ToggleButton>
      </Tooltip>
    </ToggleButtonGroup>
  );
};

// Custom replacement for react-admin's <FilterButton>. RA's button is hard-coded to
// the "Add filter" translation key, which competes with the view selector for
// primary-control status. This version uses the "More filters" label and a Menu
// listing only filters that aren't currently displayed.
const MoreFiltersButton = ({ filters }: { filters: React.ReactElement[] }) => {
  const { showFilter, displayedFilters, filterValues } = useListContext();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const hiddenFilters = filters.filter(filter => {
    const source = filter.props?.source as string | undefined;
    if (!source) return false;
    if (filter.props?.alwaysOn) return false;
    if (displayedFilters?.[source]) return false;
    if (filterValues?.[source] !== undefined && filterValues?.[source] !== '') return false;
    return true;
  });

  if (hiddenFilters.length === 0) return null;

  return (
    <>
      <Button size="small" startIcon={<FilterListIcon />} onClick={event => setAnchor(event.currentTarget)}>
        More filters
      </Button>
      <Menu open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}>
        {hiddenFilters.map(filter => {
          const source = filter.props.source as string;
          // ReferenceInput places the user-facing label on its child (e.g. AutocompleteInput),
          // not on itself — fall back to the first child's label before the raw source name.
          const childLabel = (filter.props.children as React.ReactElement | undefined)?.props?.label;
          const label = (filter.props.label as string | undefined) ?? (childLabel as string | undefined) ?? source;
          return (
            <MenuItem
              key={source}
              onClick={() => {
                showFilter(source, filter.props.defaultValue ?? '');
                setAnchor(null);
              }}
            >
              {label}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

const resolveReferenceName = (resource: string, record?: Record<string, any>) => {
  if (!record) return undefined;
  return resource === 'campaigns' ? formatCampaignTitle(record) : (record.name ?? undefined);
};

// Renders a removable filter chip. For reference filters (campaign, advertiser)
// the raw id label is upgraded to the record's name once it loads.
const FilterChip = ({ chip, onDelete }: { chip: RemovableFilterChip; onDelete: () => void }) => {
  const { reference } = chip;
  const { data } = useGetOne(
    reference?.resource ?? 'campaigns',
    { id: reference?.id ?? '' },
    { enabled: Boolean(reference?.id) },
  );

  const resolvedName = reference ? resolveReferenceName(reference.resource, data) : undefined;
  const label = reference && resolvedName ? `${reference.prefix}: ${resolvedName}` : chip.label;

  return <Chip size="small" label={label} onDelete={onDelete} />;
};

// Imported PIO rows have a non-null `external_pio_id` and land with fresh
// `created_at`/blank reviewer state during UAT reset, so Recent review queues
// must be scoped to CSAdmin-created rows.
const LineItemActiveFilterChips = () => {
  const { displayedFilters, filterValues, setFilters } = useListContext();
  const filters = (filterValues ?? {}) as LineItemFilterValues;
  const recentActive = isRecentLineItemFilterActive(filters);
  const selfReviewedActive = isSelfReviewedRecentLineItemFilterActive(filters);
  const extraFilterChips = getLineItemExtraFilterChips(filters);

  if (!recentActive && extraFilterChips.length === 0) return null;

  const sinceLabel = formatRecentWindowSince(filters.createdAt);
  const removeFilter = (source: string) => {
    const { [source]: _removedFilter, ...nextFilters } = filters;
    const nextDisplayedFilters = { ...(displayedFilters ?? {}) };
    delete nextDisplayedFilters[source];
    setFilters(nextFilters, nextDisplayedFilters);
  };

  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
        Filtered by:
      </Typography>
      {recentActive && (
        <Chip
          size="small"
          label={sinceLabel ? `CSAdmin-created ≥ ${sinceLabel}` : 'CSAdmin-created in last 7 days'}
          onDelete={() => setFilters(applyLineItemViewId(filters, LINE_ITEM_VIEW_ALL), displayedFilters)}
        />
      )}
      {selfReviewedActive && (
        <Chip
          size="small"
          label="Reviewer: blank"
          onDelete={() => setFilters(applyLineItemViewId(filters, LINE_ITEM_VIEW_RECENT), displayedFilters)}
        />
      )}
      {extraFilterChips.map(chip => (
        <FilterChip key={chip.key} chip={chip} onDelete={() => removeFilter(chip.key)} />
      ))}
    </Stack>
  );
};

const CSAPI_BASE_URL = process.env.NEXT_PUBLIC_CSAPI_BASE_URL;

const useExportCsv = (path: string, label: string) => {
  const { session } = useClerk();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleClick = async () => {
    setIsDownloading(true);
    try {
      const token = await session?.getToken();
      const response = await fetch(`${CSAPI_BASE_URL}/api/exports/${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Export failed: ${response.status}`);
      const disposition = response.headers.get('Content-Disposition') ?? '';
      const filenameMatch = disposition.match(/filename="([^"]+)"/);
      const filename = filenameMatch?.[1] ?? `${label}.csv`;
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`${label} export error`, err);
    } finally {
      setIsDownloading(false);
    }
  };

  return { handleClick, isDownloading };
};

const InvoiceExportButton = () => {
  const { handleClick, isDownloading } = useExportCsv('invoice-csv', 'Invoice_Export');
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<DownloadIcon />}
      onClick={handleClick}
      disabled={isDownloading}
    >
      {isDownloading ? 'Exporting…' : 'Invoice Export'}
    </Button>
  );
};

const CloseExportButton = () => {
  const { handleClick, isDownloading } = useExportCsv('close-csv', 'Revenue_Close');
  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<DownloadIcon />}
      onClick={handleClick}
      disabled={isDownloading}
    >
      {isDownloading ? 'Exporting…' : 'Close Export'}
    </Button>
  );
};

const LineItemListActions = () => (
  <Box sx={{ width: '100%', py: 1 }}>
    {/* Row 1 — primary controls: view picker on the left, Create + exports on the right */}
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      justifyContent="space-between"
      sx={{ width: '100%' }}
    >
      <LineItemViewSelector />
      <Stack direction="row" spacing={1}>
        <InvoiceExportButton />
        <CloseExportButton />
        <CreateButton />
      </Stack>
    </Stack>

    {/* Row 1.5 — view-driven active filter chips (only when a view scopes results) */}
    <Box sx={{ mt: 1 }}>
      <LineItemActiveFilterChips />
    </Box>

    <Divider sx={{ my: 1.5 }} />

    {/* Row 2 — secondary search/filter inputs + "More filters" menu */}
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1}
      alignItems={{ xs: 'stretch', sm: 'flex-end' }}
      sx={{ width: '100%' }}
    >
      <DataTableFilterControls filters={lineItemFilters}>
        <MoreFiltersButton filters={lineItemFilters} />
      </DataTableFilterControls>
    </Stack>
  </Box>
);

const CurrencyField = ({ source, label }: { source: string; label: string }) => (
  <FunctionField
    source={source}
    label={label}
    textAlign="right"
    render={(record: Record<string, any>) => formatCurrency(record?.[source])}
  />
);

export const LineItemList = () => (
  <List
    actions={<LineItemListActions />}
    sort={{ field: 'startDate', order: 'DESC' }}
    perPage={50}
    filterDefaultValues={{ includeArchived: false }}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <FunctionField source="name" label="Line Item" render={(record: LineItemRecord) => formatLineItemTitle(record)} />
      <ReferenceField source="campaignId" reference="campaigns" label="Campaign" link="show">
        <FunctionField render={formatCampaignTitle} />
      </ReferenceField>
      <ReferenceField source="productId" reference="products" label="Product" link={false}>
        <TextField source="name" emptyText="-" />
      </ReferenceField>
      <TextField source="specialty" label="Specialty" emptyText="-" sortable />
      <ESTDateField source="startDate" label="Start Date" textAlign="center" />
      <ESTDateField source="endDate" label="End Date" textAlign="center" />
      <FunctionField
        source="status"
        label="Status"
        render={(record: Record<string, any>) => formatChoice(lineItemStatusChoices, record?.status)}
      />
      <FunctionField
        source="invoiceStatus"
        label="Invoice Status"
        render={(record: Record<string, any>) => formatChoice(invoiceStatusChoices, record?.invoiceStatus)}
      />
      <CurrencyField source="invoiceAmount" label="Invoice Amount" />
      <CurrencyField source="revenue" label="Revenue" />
      <FunctionField
        source="reviewedBy"
        label="Reviewed By"
        sortable={false}
        render={(record: LineItemListRecord) => record?.pioUserByReviewedBy?.name ?? '-'}
      />
      <TextField source="gamLineItemId" label="GAM Line Item ID" emptyText="-" />
      <ShowButton />
      <EditButton />
      <DuplicateLineItemButton />
      <LineItemArchiveButton />
    </Datagrid>
  </List>
);
